// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useModal } from '@shared/ui-kit';
import type { SvgIconName } from '@shared/ui-kit/components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MODAL_TEMPLATES_OPTIONS,
  showModal,
  type ShowModalOptions,
} from '@shared/ui-kit/components/popups/show-modal';
import { isNullOrUndefined } from '@shared/utils';
import type {
  SourceAsset,
  TransferOperationData,
  BuyOperationData,
  SellOperationData,
  OperationData,
  AssetOperationType,
  FundSearchResult,
} from '@shared/portfolio-operations/types';
import { OperationType } from '@shared/data-access/interfaces/operation-type';

export interface AssetOperationModalProps {
  open: boolean;
  onClose: () => void;
  operation: AssetOperationType;
  sourceAsset: SourceAsset;
  onTransfer?: (data: TransferOperationData, sourceAsset: SourceAsset) => void;
  onBuy?: (data: BuyOperationData, sourceAsset: SourceAsset) => void;
  onSell?: (data: SellOperationData, sourceAsset: SourceAsset) => void;
  onCancel?: () => void;
  portfolioFunds?: FundSearchResult[];
}

const ICON_CONFIG = {
  BUY: { icon: 'Trader' as SvgIconName, color: 'success' as const },
  SELL: { icon: 'TrendingDown' as SvgIconName, color: 'danger' as const },
  TRANSFER: { icon: 'ArrowLeftRight' as SvgIconName, color: 'main' as const },
} as const;

type ModalSyncEvent = 'open' | 'close' | null;

export const useAssetOperationModal = (props: AssetOperationModalProps) => {
  const { open, onClose, operation, sourceAsset, onTransfer, onBuy, onSell } =
    props;
  const { t } = useTranslation();

  const [Modal, modal] = useModal({
    component: 'dialog',
    defaultOpen: open,
    afterClose: useCallback(() => {
      onClose();
    }, [onClose]),
  });

  const modalSyncEvent = useMemo<ModalSyncEvent>(() => {
    if (open && !modal.open) return 'open';
    if (!open && modal.open) return 'close';
    return null;
  }, [open, modal.open]);

  useEffect(() => {
    switch (modalSyncEvent) {
      case 'open':
        modal.onOpen();
        break;
      case 'close':
        modal.onClose();
        break;
    }
  }, [modalSyncEvent, modal]);

  const [formData, setFormData] = useState<
    TransferOperationData | BuyOperationData | SellOperationData | null
  >(null);

  const [isFormValid, setIsFormValid] = useState(false);

  const { icon, color } = useMemo(() => ICON_CONFIG[operation], [operation]);

  const onTransferConfirm = useCallback(
    (data: TransferOperationData) => {
      onTransfer?.(data, sourceAsset);
      modal.onClose();
    },
    [onTransfer, sourceAsset, modal],
  );

  const onBuyConfirm = useCallback(
    (data: BuyOperationData) => {
      onBuy?.(data, sourceAsset);

      modal.onClose();
    },
    [onBuy, sourceAsset, modal],
  );

  const onSellConfirm = useCallback(
    (data: SellOperationData) => {
      onSell?.(data, sourceAsset);

      modal.onClose();
    },
    [onSell, sourceAsset, modal],
  );

  const onFormDataChange = useCallback((data: OperationData) => {
    setFormData(data);
  }, []);

  const onFormValidationChange = useCallback((isValid: boolean) => {
    setIsFormValid(isValid);
  }, []);

  const onConfirmClick = useCallback(async () => {
    if (!formData || !isFormValid) return;

    const isMutualFundAsset = ['mutual_fund', 'fund'].includes(
      sourceAsset.assetType,
    );

    const isInversisBuyAssetNotAvailable =
      operation === OperationType.BUY &&
      isMutualFundAsset &&
      isNullOrUndefined(sourceAsset.dfiUrl);

    const isInversisDestinationFundsNotAvailable =
      operation === OperationType.TRANSFER &&
      isMutualFundAsset &&
      isNullOrUndefined(sourceAsset.dfiUrl) &&
      (formData as TransferOperationData).destinationFunds.some(fund =>
        isNullOrUndefined(fund.dfi_url),
      );

    if (isInversisBuyAssetNotAvailable || isInversisDestinationFundsNotAvailable) {
      const { isConfirmed } = await showModal({
        ...MODAL_TEMPLATES_OPTIONS(t).WARN,
        title: t('ticker-modal.invalid-proposal-asset-title', {
          ns: 'components',
        }),
        content: t('ticker-modal.invalid-proposal-asset-message', {
          ns: 'components',
          ticker: sourceAsset.ticker ?? sourceAsset.isin ?? sourceAsset.name,
        }),
        buttons: {
          cancel: {
            show: true,
            text: t('common.cancel', { ns: 'common' }),
          },
          confirm: {
            show: true,
            text: t('ticker-modal.invalid-proposal-asset-continue', {
              ns: 'components',
            }),
          },
        },
      } as ShowModalOptions);

      if (!isConfirmed) return;
    }

    switch (operation) {
      case OperationType.TRANSFER:
        onTransfer && onTransferConfirm(formData as TransferOperationData);
        break;
      case OperationType.BUY:
        onBuy && onBuyConfirm(formData as BuyOperationData);
        break;
      case OperationType.SELL:
        onSell && onSellConfirm(formData as SellOperationData);
        break;
      default:
        break;
    }
  }, [
    formData,
    isFormValid,
    operation,
    onTransfer,
    onBuy,
    onSell,
    onTransferConfirm,
    onBuyConfirm,
    onSellConfirm,
    sourceAsset,
    t,
  ]);

  const isConfirmDisabled = useMemo(() => {
    if (!isFormValid || !formData) return true;

    switch (operation) {
      case OperationType.TRANSFER:
        return (
          !onTransfer ||
          (formData as TransferOperationData).destinationFunds.length === 0
        );
      case OperationType.BUY:
        return !onBuy || (formData as BuyOperationData).amount <= 0;
      case OperationType.SELL:
        return !onSell || (formData as SellOperationData).amount <= 0;
      default:
        return false;
    }
  }, [isFormValid, formData, operation, onTransfer, onBuy, onSell]);

  return {
    Modal,
    modal,
    icon,
    color,
    operation,
    sourceAsset,
    onCancel: props.onCancel,
    portfolioFunds: props.portfolioFunds,
    onTransferConfirm,
    onBuyConfirm,
    onSellConfirm,
    onFormDataChange,
    onFormValidationChange,
    onConfirmClick,
    isConfirmDisabled,
  };
};
